import { Card, Circle, Heading, HStack, Icon, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react'
import { usePitchDeckUpload } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckUpload'
import { UploadDocumentButton } from '@src/components/sco/UploadDocumentButton'
import { scoBackendUrl } from '@src/lib/startups-url.ts'
import { useState } from 'react'
import { LuGauge, LuMessagesSquare, LuTriangleAlert, LuUpload } from 'react-icons/lu'

const STEPS = [
  { icon: LuUpload, title: 'Upload', body: 'Drop a deck (PDF). We pull every slide.' },
  {
    icon: LuGauge,
    title: 'Audit',
    body: 'Each slide is graded against the 9-slide framework and rubric rules.',
  },
  {
    icon: LuMessagesSquare,
    title: 'Read the verdict',
    body: 'Scores, rewrites, and a coach to argue with — slide by slide.',
  },
]

export function PitchDeckStart({ onCreated }: { onCreated: (pitchDeckId: string) => void }) {
  const { createDeckFromStoredFile, creating } = usePitchDeckUpload()
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const handleUploaded = async (_url: string, storedFileId?: string) => {
    setError(null)
    if (!storedFileId) {
      setError('Upload succeeded but no file id was returned. Please try again.')
      return
    }
    setWorking(true)
    try {
      const pitchDeckId = await createDeckFromStoredFile(storedFileId, { analyze: true })
      if (pitchDeckId) {
        onCreated(pitchDeckId)
      } else {
        setError('We could not start the audit. Please try again.')
        setWorking(false)
      }
    } catch (e: any) {
      setError(e?.message || 'Something went wrong starting the audit.')
      setWorking(false)
    }
  }

  const busy = creating || working

  return (
    <VStack gap={4} marginX={'auto'} maxWidth={'container.md'} paddingY={{ base: 4, md: 8 }} width={'full'}>
      <VStack gap={3} pt={{ base: 2, md: 8 }} textAlign={'center'}>
        <Text color={'fg.subtle'} letterSpacing={'widest'} textStyle={'labelXs'}>
          Startups.com Pitch Deck Auditor
        </Text>
        <Heading size={{ base: '2xl', md: '3xl' }}>Upload your deck. Get an honest audit.</Heading>
        <Text color={'fg.muted'} maxWidth={'container.sm'}>
          No fluff. Slide by slide, in about a minute. We tell you what&rsquo;s working, what&rsquo;s broken,
          and how to fix it.
        </Text>
      </VStack>

      <Card.Root
        _hover={{ borderColor: 'orange.solid' }}
        borderColor={'border.emphasized'}
        borderStyle={'dashed'}
        borderWidth={'2px'}
        transition={'all 0.15s ease'}
        width={'full'}
      >
        <Card.Body py={10}>
          <VStack gap={2}>
            <Circle bg={'orange.subtle'} size={14}>
              <Icon as={busy ? Spinner : LuUpload} boxSize={6} color={'orange.fg'} />
            </Circle>
            <Heading size={'md'}>{busy ? 'Starting your audit…' : 'Upload your deck'}</Heading>
            <Text color={'fg.muted'} fontSize={'sm'} textAlign={'center'}>
              PDF up to 40&nbsp;MB. (Export PowerPoint / Keynote to PDF first.)
            </Text>
            <UploadDocumentButton
              allowedExtensions={['.pdf']}
              loading={busy}
              loadingText={'Starting…'}
              maxSizeInBytes={50 * 1024 * 1024}
              size={'lg'}
              storedFile={{ attachmentType: 'PitchDeck', contextKey: 'original' }}
              uploadUrl={scoBackendUrl('/assets/upload-pitch-deck')}
              variant={'cta'}
              onError={(e: any) => setError(e?.message || 'Upload failed. Please try again.')}
              onSuccess={handleUploaded}
            >
              <LuUpload />
              Choose a PDF
            </UploadDocumentButton>
            {error && (
              <HStack color={'red.fg'} fontSize={'sm'} gap={2}>
                <Icon as={LuTriangleAlert} boxSize={4} />
                <Text>{error}</Text>
              </HStack>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} width={'full'}>
        {STEPS.map(step => (
          <Card.Root key={step.title} size={'sm'} variant={'subtle'}>
            <Card.Body>
              <HStack gap={2} mb={1}>
                <Icon as={step.icon} boxSize={4} color={'orange.fg'} />
                <Text fontSize={'sm'} fontWeight={'semibold'}>
                  {step.title}
                </Text>
              </HStack>
              <Text color={'fg.muted'} fontSize={'xs'}>
                {step.body}
              </Text>
            </Card.Body>
          </Card.Root>
        ))}
      </SimpleGrid>
    </VStack>
  )
}
